// R-14: CalendarPage - 메인 오케스트레이터 (뷰 컴포넌트, 유틸, 폴링 훅 분리)
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { accountAPI, postAPI, keywordAPI, generationRuleAPI } from '../../services/api'
import { usePolling } from './hooks/usePolling'
import {
  groupNewsByDate, generateCalendarDays, fmtDate, getWeekDays,
  statusBadge, getNewsDate, getNewsTime, getTodayStr, getDefaultDates, MONTH_NAMES,
} from './calendarUtils'
import GenerationRulesPanel from './components/GenerationRulesPanel'
import PostDetailModal from './components/PostDetailModal'
import CreatePostPanel from './components/CreatePostPanel'
import DatePostsModal from './components/DatePostsModal'
import MonthlyView from './components/MonthlyView'
import WeeklyView from './components/WeeklyView'
import DailyView from './components/DailyView'

function CalendarPage() {
  const { user } = useAuth()
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth())
  const [newsList, setNewsList] = useState([])
  const [newsMap, setNewsMap] = useState({})
  const [account, setAccount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('monthly')
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d
  })
  const [currentDay, setCurrentDay] = useState(new Date())

  // 생성 규칙 관련 state
  const [rulesPanelOpen, setRulesPanelOpen] = useState(false)
  const [generationRule, setGenerationRule] = useState(null)
  const [ruleForm, setRuleForm] = useState({
    generationMode: 'DAILY', dailyCount: 1, startDate: '', endDate: '',
    daysAhead: 3, autoGenerate: false,
  })
  const [ruleSaving, setRuleSaving] = useState(false)

  // 모달 state
  const [selectedPost, setSelectedPost] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedDatePosts, setSelectedDatePosts] = useState([])
  const [isPublishing, setIsPublishing] = useState(false)
  const [showCreatePanel, setShowCreatePanel] = useState(false)

  // 키워드
  const [keywords, setKeywords] = useState([])

  const todayStr = getTodayStr()
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const tomorrowStr = fmtDate(tomorrow)

  // 폴링
  const pollFn = useCallback(async () => {
    if (!account?.id || !generationRule?.id) return false
    const [ruleRes, newsRes] = await Promise.all([
      generationRuleAPI.get(account.id),
      postAPI.getByAccount(account.id)
    ])
    if (newsRes.success && newsRes.data) setNewsList(newsRes.data)
    if (ruleRes.success && ruleRes.data) {
      setGenerationRule(ruleRes.data)
      return ruleRes.data.status !== 'RUNNING'
    }
    return false
  }, [account?.id, generationRule?.id])

  const { pollingActive, startPolling, stopPolling } = usePolling(pollFn)

  useEffect(() => {
    if (generationRule?.status === 'RUNNING') startPolling()
    else stopPolling()
  }, [generationRule?.status, startPolling, stopPolling])

  useEffect(() => {
    if (user?.username) loadCalendarData()
  }, [user])

  useEffect(() => {
    setNewsMap(groupNewsByDate(newsList))
  }, [newsList, currentYear, currentMonth])

  const loadCalendarData = async () => {
    try {
      setLoading(true)
      const accountRes = await accountAPI.getMy()
      if (!accountRes.success || !accountRes.data?.length) { setLoading(false); return }
      const myAccount = accountRes.data.find(a => a.status === 'ACTIVE' && a.platformUserId) || accountRes.data[0]
      setAccount(myAccount)
      accountRes.data = myAccount

      const [newsRes, ruleRes, kwRes] = await Promise.all([
        postAPI.getByAccount(accountRes.data.id),
        generationRuleAPI.get(accountRes.data.id).catch(() => ({ success: true, data: null })),
        keywordAPI.getByAccountId(accountRes.data.id).catch(() => ({ success: true, data: [] }))
      ])

      if (newsRes.success && newsRes.data) setNewsList(newsRes.data)
      if (kwRes.success && kwRes.data) {
        setKeywords(Array.isArray(kwRes.data) ? kwRes.data : kwRes.data.content || [])
      }

      if (ruleRes.success && ruleRes.data) {
        setGenerationRule(ruleRes.data)
        setRuleForm({
          generationMode: ruleRes.data.generationMode, dailyCount: ruleRes.data.dailyCount,
          startDate: ruleRes.data.startDate || '', endDate: ruleRes.data.endDate || '',
          daysAhead: ruleRes.data.daysAhead || 3, autoGenerate: ruleRes.data.autoGenerate || false,
        })
        setRulesPanelOpen(false)
      } else {
        const defaults = getDefaultDates()
        setRuleForm(prev => ({ ...prev, startDate: defaults.startDate, endDate: defaults.endDate }))
        setRulesPanelOpen(true)
      }
    } catch (err) {
      alert('캘린더 데이터 로드 실패: ' + (err.message || '알 수 없는 오류'))
    } finally {
      setLoading(false)
    }
  }

  const isInRange = (dateStr) => {
    if (!ruleForm.startDate || !ruleForm.endDate) return false
    return dateStr >= ruleForm.startDate && dateStr <= ruleForm.endDate
  }

  // --- Navigation ---
  const goToPrevMonth = () => { if (currentMonth === 0) { setCurrentYear(currentYear - 1); setCurrentMonth(11) } else setCurrentMonth(currentMonth - 1) }
  const goToNextMonth = () => { if (currentMonth === 11) { setCurrentYear(currentYear + 1); setCurrentMonth(0) } else setCurrentMonth(currentMonth + 1) }
  const goToPrevWeek = () => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(d) }
  const goToNextWeek = () => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(d) }
  const goToPrevDay = () => { const d = new Date(currentDay); d.setDate(d.getDate() - 1); setCurrentDay(d) }
  const goToNextDay = () => { const d = new Date(currentDay); d.setDate(d.getDate() + 1); setCurrentDay(d) }

  // --- Modal handlers ---
  const openPostDetail = (post) => { setSelectedPost(post); setSelectedDate(null) }
  const openDatePosts = (dateStr, posts) => { setSelectedDate(dateStr); setSelectedDatePosts(posts); setSelectedPost(null) }
  const closeModal = () => { setSelectedPost(null); setSelectedDate(null); setSelectedDatePosts([]); setShowCreatePanel(false) }
  const openCreatePanel = () => { setShowCreatePanel(true); setSelectedPost(null); setSelectedDate(null); setSelectedDatePosts([]) }

  const handleUpdatePost = (postId, updatedPost) => {
    if (updatedPost === null) {
      setNewsList(prev => prev.filter(n => n.id !== postId))
      setSelectedDatePosts(prev => prev.filter(p => p.id !== postId))
    } else {
      setNewsList(prev => prev.map(p => p.id === postId ? updatedPost : p))
      setSelectedPost(updatedPost)
    }
  }

  // --- Rule handlers ---
  const handleSaveRule = async () => {
    if (!account) return
    try {
      setRuleSaving(true)
      const res = await generationRuleAPI.save(account.id, ruleForm)
      if (res.success) { setGenerationRule(res.data); setRulesPanelOpen(false) }
    } catch (err) { alert('저장 실패: ' + err.message) }
    finally { setRuleSaving(false) }
  }

  const handleExecute = async () => {
    if (!account) return
    if (!confirm('생성을 시작하시겠습니까? (설정이 자동 저장됩니다)')) return
    try {
      setRuleSaving(true)
      const saveRes = await generationRuleAPI.save(account.id, ruleForm)
      setRuleSaving(false)
      if (!saveRes.success || !saveRes.data) { alert('규칙 저장 실패'); return }
      setGenerationRule(saveRes.data)
      const res = await generationRuleAPI.execute(saveRes.data.id)
      if (res.success && res.data) setGenerationRule(res.data)
    } catch (err) { setRuleSaving(false); alert('생성 실행 실패: ' + err.message) }
  }

  const handleResetStatus = async () => {
    if (!generationRule?.id) return
    if (!confirm('상태를 초기화(대기)로 변경하시겠습니까?')) return
    try {
      const res = await generationRuleAPI.resetStatus(generationRule.id)
      setGenerationRule(res.data)
      alert('상태가 초기화되었습니다.')
    } catch (err) { alert('상태 초기화 실패: ' + err.message) }
  }

  // --- Generation Rules Panel ---
  const rulesPanel = GenerationRulesPanel({
    account, generationRule, ruleForm, setRuleForm,
    rulesPanelOpen, setRulesPanelOpen,
    onSave: handleSaveRule, onExecute: handleExecute, onResetStatus: handleResetStatus, ruleSaving,
    minStartDate: tomorrowStr
  })

  const calendarDays = generateCalendarDays(currentYear, currentMonth)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-500 dark:text-slate-400 text-lg">로딩 중...</div>
      </div>
    )
  }

  if (!account) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-600">calendar_month</span>
        <p className="text-slate-500 dark:text-slate-400 text-lg">연결된 인스타그램 계정이 없습니다</p>
        <p className="text-slate-400 dark:text-slate-500 text-sm">자동화 설정에서 계정을 먼저 등록해주세요</p>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* 인스타그램 업로드 전체 화면 인디케이터 */}
      {isPublishing && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4 max-w-sm mx-4">
            <div className="relative">
              <div className="size-16 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 animate-spin" style={{ clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 50% 100%, 50% 15%, 85% 15%, 85% 85%, 50% 85%)' }} />
              <span className="material-symbols-outlined text-3xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-pink-500">send</span>
            </div>
            <p className="text-lg font-bold dark:text-white">인스타그램에 업로드 중...</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center">잠시만 기다려주세요.<br/>업로드가 완료되면 자동으로 닫힙니다.</p>
          </div>
        </div>
      )}

      {/* 상단: 헤더 + 뷰모드 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">event_note</span>
            <h2 className="text-base sm:text-lg font-bold dark:text-white">콘텐츠 스케줄러</h2>
          </div>
          <div className="flex bg-slate-200 dark:bg-slate-800 rounded-full p-1">
            {['monthly', 'weekly', 'daily'].map((mode) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                className={`px-3 sm:px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold transition-all ${viewMode === mode ? 'bg-white dark:bg-slate-700 shadow-sm dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`}>
                {mode === 'monthly' ? '월간' : mode === 'weekly' ? '주간' : '일간'}
              </button>
            ))}
          </div>
          <button onClick={openCreatePanel}
            className="hidden lg:flex px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:opacity-90 transition-opacity items-center gap-1">
            <span className="material-symbols-outlined text-sm">add</span> 새 게시물
          </button>
        </div>
      </div>

      {rulesPanel.render}

      {/* 생성 진행 중 표시 */}
      {pollingActive && (
        <div className="mb-4 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="size-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">콘텐츠 생성 중...</span>
            {generationRule?.totalCount > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-500">
                {generationRule.successCount || 0}/{generationRule.totalCount}건 완료
                {(generationRule.failCount || 0) > 0 && `, ${generationRule.failCount}건 실패`}
              </span>
            )}
            <span className="text-xs text-amber-500 dark:text-amber-600 ml-auto">5초마다 자동 갱신</span>
          </div>
          {generationRule?.totalCount > 0 && (
            <div className="mt-2 w-full bg-amber-200 dark:bg-amber-800 rounded-full h-1.5">
              <div
                className="bg-amber-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.round(((generationRule.successCount || 0) + (generationRule.failCount || 0)) / generationRule.totalCount * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* 생성 실패 표시 */}
      {generationRule?.status === 'FAILED' && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <span className="material-symbols-outlined text-red-500 text-lg">error</span>
          <span className="text-sm font-semibold text-red-700 dark:text-red-400">
            생성 실패 - {generationRule.successCount || 0}/{generationRule.totalCount || 0}건 성공, {generationRule.failCount || 0}건 실패 (재시도 3회 초과)
          </span>
          <button onClick={handleResetStatus}
            className="ml-auto px-3 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors dark:bg-red-900/30 dark:text-red-400">
            상태 초기화
          </button>
        </div>
      )}

      {/* 하단: 좌측 상세 + 우측 달력 */}
      <div className="flex gap-6">

        {/* PC: 좌측 패널 - 상세보기 또는 새 게시물 등록 */}
        {selectedPost && (
          <div className="hidden lg:block lg:w-[440px] lg:flex-shrink-0 self-stretch">
            <div className="h-full overflow-y-auto">
              <PostDetailModal
                selectedPost={selectedPost} onClose={closeModal} onUpdatePost={handleUpdatePost}
                statusBadge={statusBadge} getNewsDate={getNewsDate} getNewsTime={getNewsTime}
                accountId={account?.id} inline
                onPublishStateChange={setIsPublishing}
              />
            </div>
          </div>
        )}
        {showCreatePanel && !selectedPost && (
          <div className="hidden lg:block lg:w-[440px] lg:flex-shrink-0 self-stretch">
            <div className="h-full overflow-y-auto">
              <CreatePostPanel
                keywords={keywords} account={account}
                selectedDate={selectedDate}
                onClose={closeModal}
                onCreated={(newPost) => {
                  setNewsList(prev => [...prev, newPost])
                  setShowCreatePanel(false)
                }}
                inline
              />
            </div>
          </div>
        )}

        {/* 우측: 달력 영역 */}
        <div className="flex-1 min-w-0">

          {/* Navigation */}
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <h3 className="text-lg sm:text-2xl font-bold dark:text-white">
                {viewMode === 'monthly' && `${currentYear}년 ${MONTH_NAMES[currentMonth]}`}
                {viewMode === 'weekly' && (() => {
                  const wDays = getWeekDays(currentWeekStart)
                  const s = wDays[0], e = wDays[6]
                  return `${s.getMonth() + 1}/${s.getDate()} - ${e.getMonth() + 1}/${e.getDate()}`
                })()}
                {viewMode === 'daily' && `${currentDay.getFullYear()}년 ${currentDay.getMonth() + 1}월 ${currentDay.getDate()}일 (${['일','월','화','수','목','금','토'][currentDay.getDay()]})`}
              </h3>
              <div className="flex gap-1">
                <button onClick={viewMode === 'monthly' ? goToPrevMonth : viewMode === 'weekly' ? goToPrevWeek : goToPrevDay}
                  className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded transition-colors">
                  <span className="material-symbols-outlined dark:text-white">chevron_left</span>
                </button>
                <button onClick={viewMode === 'monthly' ? goToNextMonth : viewMode === 'weekly' ? goToNextWeek : goToNextDay}
                  className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded transition-colors">
                  <span className="material-symbols-outlined dark:text-white">chevron_right</span>
                </button>
              </div>
            </div>
            {viewMode !== 'monthly' && (
              <button onClick={() => { viewMode === 'weekly' ? setCurrentWeekStart(() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d }) : setCurrentDay(new Date()) }}
                className="px-3 py-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors">
                오늘
              </button>
            )}
          </div>

          {viewMode === 'monthly' && (
            <MonthlyView
              calendarDays={calendarDays} newsMap={newsMap}
              currentYear={currentYear} currentMonth={currentMonth}
              todayStr={todayStr} selectedDateStr={selectedDate}
              rulesPanelOpen={rulesPanelOpen}
              isInRange={isInRange} previewMap={rulesPanel.previewMap}
              openPostDetail={openPostDetail} openDatePosts={openDatePosts}
              onSelectDate={(dateStr) => setSelectedDate(dateStr)}
            />
          )}

          {viewMode === 'weekly' && (
            <WeeklyView
              currentWeekStart={currentWeekStart} newsMap={newsMap}
              todayStr={todayStr} rulesPanelOpen={rulesPanelOpen}
              isInRange={isInRange} previewMap={rulesPanel.previewMap}
              openPostDetail={openPostDetail} openDatePosts={openDatePosts}
            />
          )}

          {viewMode === 'daily' && (
            <DailyView
              currentDay={currentDay} newsMap={newsMap}
              todayStr={todayStr} previewMap={rulesPanel.previewMap}
              openPostDetail={openPostDetail} openDatePosts={openDatePosts}
            />
          )}

          {/* Summary Footer */}
          <div className="mt-6 flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-2">
              <span className="size-2.5 rounded-full bg-emerald-500"></span>
              완료 {newsList.filter(n => n.uploadStatus === 'UPLOADED').length}건
            </div>
            <div className="flex items-center gap-2">
              <span className="size-2.5 rounded-full bg-amber-400"></span>
              대기 {newsList.filter(n => n.uploadStatus === 'PENDING').length}건
            </div>
            <div className="flex items-center gap-2">
              <span className="size-2.5 rounded-full bg-red-400"></span>
              실패 {newsList.filter(n => n.uploadStatus === 'FAILED').length}건
            </div>
            <div className="ml-auto text-xs">총 {newsList.length}건의 게시물</div>
          </div>

          {/* 모바일 Post Detail Modal */}
          {selectedPost && (
            <div className="lg:hidden">
              <PostDetailModal
                selectedPost={selectedPost} onClose={closeModal} onUpdatePost={handleUpdatePost}
                statusBadge={statusBadge} getNewsDate={getNewsDate} getNewsTime={getNewsTime}
                accountId={account?.id}
                onPublishStateChange={setIsPublishing}
              />
            </div>
          )}

          {/* 모바일 Create Post Modal */}
          {showCreatePanel && !selectedPost && (
            <div className="lg:hidden">
              <CreatePostPanel
                keywords={keywords} account={account}
                selectedDate={selectedDate}
                onClose={closeModal}
                onCreated={(newPost) => {
                  setNewsList(prev => [...prev, newPost])
                  setShowCreatePanel(false)
                }}
              />
            </div>
          )}

          {/* Date Posts Modal */}
          {selectedDate && (
            <DatePostsModal
              selectedDate={selectedDate} selectedDatePosts={selectedDatePosts}
              setSelectedDatePosts={setSelectedDatePosts} keywords={keywords}
              account={account} onClose={closeModal} onPostClick={openPostDetail}
              onAddPost={(newPost) => setNewsList(prev => [...prev, newPost])}
              statusBadge={statusBadge} getNewsTime={getNewsTime}
            />
          )}
        </div>{/* /우측 달력 영역 */}
      </div>{/* /하단 flex */}
    </div>
  )
}

export default CalendarPage
